import { Badge } from '@/components/Badge'
import { cn } from '@repo/ui/lib/utils'
import { XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type FilterBadgeProps = {
  filter: string
  value: unknown
  className?: string
  onClick?: () => void
  // Only the badges that know which search params they own can be dismissed; the ones a page builds
  // by hand pass nothing and render no clear button.
  onClear?: () => void
}

export function FilterBadge(props: FilterBadgeProps) {
  const { t } = useTranslation('common')

  const content = (() => {
    // A badge with no value (e.g. "Pricing assumptions (2)") carries everything in its label.
    if (props.value === undefined) return props.filter

    if (Array.isArray(props.value)) {
      if (props.value.length > 1) return `${props.filter} (${props.value.length})`
      return (
        <>
          {props.filter}: {props.value[0]}
        </>
      )
    }

    return (
      <>
        {props.filter}: {props.value}
      </>
    )
  })()

  return (
    <Badge
      inverted
      className={cn(
        'bg-muted-foreground select-none gap-0.5 rounded-full px-2.5 font-sans font-normal',
        props.onClear && 'pr-1.5',
        props.className,
      )}
    >
      {/* The label keeps the badge's original job — reopening the filter panel. */}
      <button
        type="button"
        onClick={props.onClick}
        className="focus-visible:ring-ring cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2"
      >
        {content}
      </button>

      {props.onClear && (
        <button
          type="button"
          onClick={props.onClear}
          aria-label={t('datatable.clearFilter', { name: props.filter })}
          // A bare X set off from the label, padded to a usable hit target; hover only lifts its opacity.
          className="focus-visible:ring-ring -my-0.5 cursor-pointer rounded-full p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2"
        >
          <XIcon className="size-3" aria-hidden />
        </button>
      )}
    </Badge>
  )
}
